import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const SOURCE_ID = 'ray-bundle-discount-sync';
const COMPANION_KEY = '_ray_bundle_discount_key';

const selectors = {
  cartData: '[data-ray-bundle-cart-data]',
};

let isSyncing = false;
let rerunRequested = false;
let lastCartDataText = null;
let pendingSourceId = null;

const cartRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Ray bundle discount sync: ${url} failed (${response.status})`);
  }

  return response.json();
};

const setLineProperties = (lineKey, quantity, properties) =>
  cartRequest('/cart/change.js', { id: lineKey, quantity, properties });

const fetchCart = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`Ray bundle discount sync: /cart.js failed (${response.status})`);
  }

  return response.json();
};

const announceCartUpdate = (cart) => {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      source: SOURCE_ID,
      itemCount: cart.item_count,
    })
  );
};

/**
 * @returns {{eligiblePairs: Array<{productId: number, key: string, value: string}>, lines: Array<{productId: number, variantId: number, lineKey: string, quantity: number, storedKey: string | null, currentValue: string | null}>} | null}
 */
const readCartData = () => {
  const node = document.querySelector(selectors.cartData);

  if (!node?.textContent) return null;

  try {
    return JSON.parse(node.textContent.trim());
  } catch (error) {
    console.error('Ray bundle discount sync: cart data JSON parse failed', error);
    return null;
  }
};

/**
 * Re-checks every current cart line against the freshly computed eligibility set, and
 * attaches/corrects/removes the discount property as needed.
 * @param {ReturnType<typeof readCartData>} data
 */
const reconcile = async (data) => {
  if (!data) return;

  if (isSyncing) {
    rerunRequested = true;
    return;
  }

  const { eligiblePairs } = data;
  let lines = data.lines;

  if (!Array.isArray(lines) || lines.length === 0) return;

  const requiredPair = (productId) =>
    eligiblePairs?.find((pair) => pair.productId === productId) || null;

  isSyncing = true;

  try {
    let freshCart = null;

    const mismatchedLines = lines.filter((line) => {
      const required = requiredPair(line.productId);

      return (
        (required?.key || null) !== (line.storedKey || null) || (required?.value || null) !== (line.currentValue || null)
      );
    });

    for (const line of mismatchedLines) {
      const required = requiredPair(line.productId);
      const desiredKey = required?.key || null;
      const desiredValue = required?.value || null;

      try {
        const liveCart = await fetchCart();
        const liveLine = liveCart.items.find((item) => {
          if (item.variant_id !== line.variantId) return false;

          const storedKey = item.properties?.[COMPANION_KEY] || null;
          const currentValue = storedKey ? item.properties?.[storedKey] || null : null;

          return storedKey !== desiredKey || currentValue !== desiredValue;
        });

        if (!liveLine) continue;

        const properties = { ...(liveLine.properties || {}) };
        const previousKey = properties[COMPANION_KEY];

        if (previousKey) properties[previousKey] = '';
        properties[COMPANION_KEY] = '';

        if (desiredKey) {
          properties[desiredKey] = desiredValue;
          properties[COMPANION_KEY] = desiredKey;
        }

        freshCart = await setLineProperties(liveLine.key, liveLine.quantity, properties);
      } catch (error) {
        console.error('Ray bundle discount sync: failed to sync one line', error);
      }
    }

    if (freshCart) announceCartUpdate(freshCart);
  } catch (error) {
    console.error('Ray bundle discount sync: failed to sync discount property', error);
  } finally {
    isSyncing = false;
  }

  if (rerunRequested) {
    rerunRequested = false;
    lastCartDataText = document.querySelector(selectors.cartData)?.textContent?.trim() ?? null;
    await reconcile(readCartData());
  }
};

lastCartDataText = document.querySelector(selectors.cartData)?.textContent?.trim() ?? null;

const checkForFreshData = () => {
  const currentText = document.querySelector(selectors.cartData)?.textContent?.trim() ?? null;

  if (currentText === lastCartDataText) return;

  lastCartDataText = currentText;

  const sourceId = pendingSourceId;

  pendingSourceId = null;

  if (sourceId === SOURCE_ID) return;

  reconcile(readCartData());
};

new MutationObserver(checkForFreshData).observe(document.body, {
  childList: true,
  subtree: true,
  characterData: true,
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId) pendingSourceId = event.detail.sourceId;
});

reconcile(readCartData());
